import { Check, Loader2 } from "lucide-react";
import type { CatalogueItemPricePreview } from "@shared-core/data/catalogue-types";
import type {
  PriceLevelKind,
  PriceLevels,
} from "@shared-core/data/price-precedent-types";
import type { PendingPriceRoute } from "@shared-core/data/supplier-pricing-types";
import { formatQuotedPrice } from "@shared-core/domain/formatters";

// The item's quantity chooser and the pricing it reads. Lifted from the
// catalogue item page so One's item configuration widget draws the same
// ladder from its dry run's price.

/** What the ladder reads of the orderer's choices: the quantity, and the price level in force. */
export interface LadderSelection {
  quantity: number;
  /** A typed quantity rather than one of the item's breaks. */
  customQty: boolean;
  /** The chosen price level; null follows the suggestion. */
  priceLevel: PriceLevelKind | null;
  /** The custom level's price in pence; null until typed. */
  customPricePence: number | null;
}

export function perUnitLabel(perUnit: number): string {
  return `£${perUnit.toFixed(2)}`;
}

/**
 * The one resolution of the item's price, shared by the headline and the ladder.
 * There is no seeded initial value, and no *silent* fallback: a failed
 * resolution ("error") renders an explicit error and no price. A route that
 * holds no price is not an option, and nothing asks Tharstern for one on
 * demand (order to invoice doc 107 §2a, §2b).
 */
export type ItemPricingState =
  | { status: "loading" }
  | {
      status: "ready";
      preview: CatalogueItemPricePreview;
      /** How much of the quantity ladder `preview` actually carries.
       *
       *  The page asks for the selected quantity first (`ladder: false`) so
       *  the headline renders without waiting on nine quantities nobody has
       *  asked about, then asks for the rest. Between those two answers the
       *  state is "loading": the headline and the selected row are real, and
       *  every other rung is still coming. "failed" is the second call
       *  falling over — the headline stays on screen and the rungs it never
       *  delivered read as unpriced, which is what a dash has always meant
       *  here. */
      ladderStatus?: "loading" | "ready" | "failed";
    }
  | { status: "error" }
  // Every route that could price this is still pricing (live supplier pricing §6.2): nothing to show but who, and
  // the page asks again. A route still pricing beside a priced one is `ready`, in `priceOptions.pending`.
  | { status: "pending"; pending: readonly PendingPriceRoute[] }
  // Order again pre-fill only (brief §16): at least one field is still tier-
  // "unresolved" — no price-preview call is made at all until the CSR
  // resolves every field, so nothing can misprice off an incomplete spec.
  | { status: "context" }
  | { status: "blocked"; unresolvedCount: number }
  // The versions' quantities cannot be shared yet (an even split that does not fit, quantities that do not add up),
  // or the backend refused them: no price is asked for until they are settled (order to invoice doc 108 §3.1).
  | { status: "versions_unsettled" };

/**
 * The price in force for a quantity: the CSR's chosen level, or the
 * suggested one when they have not chosen. Custom applies only to the
 * headline quantity (`isHeadline`); other rungs fall back to their own
 * suggestion so the ladder never shows a typed figure against the wrong
 * quantity. Without levels (no preview yet, an uncosted rung) it is the
 * policy price.
 */
export function levelPence(
  policyPence: number,
  levels: PriceLevels | undefined,
  chosen: PriceLevelKind | null,
  customPricePence: number | null,
  isHeadline: boolean,
): { pence: number; level: PriceLevelKind } {
  if (chosen === "custom" && isHeadline && customPricePence != null) {
    return { pence: customPricePence, level: "custom" };
  }
  const kind =
    chosen === "custom" || chosen == null
      ? (levels?.suggested ?? "policy")
      : chosen;
  const level = levels?.levels.find((l) => l.kind === kind);
  return level
    ? { pence: level.price_pence, level: kind }
    : { pence: policyPence, level: "policy" };
}

/**
 * The quantity chooser. Quantities are the item's own standard breaks; every
 * price in it comes from the price-preview response, the same one the headline
 * renders — so the two cannot disagree.
 *
 * This table used to render `item.quantityBreaks[].price`, the static list
 * prices, and showed £85 for 1,000 letterheads beside a headline of £181 for
 * the same 1,000, unmoved by the Print option that had produced the £181.
 *
 * A row whose (variant × quantity) has no cached manufacturing cost shows "not
 * costed" and no figure. Pricing still falls back to the list price internally
 * — the send path needs a base — but those prices predate the cost model and
 * several sit below what the job costs to make, so they are not an answer to
 * offer for a quantity someone is browsing. The row stays selectable: selecting
 * it puts the explanation and the Refresh action next to the headline.
 */
export function ItemQuantityLadder({
  quantityBreaks,
  selection,
  onSelect,
  pricing,
}: {
  /** The item's standard quantity breaks. */
  quantityBreaks: readonly number[];
  selection: LadderSelection;
  onSelect: (next: { quantity: number; customQty: boolean }) => void;
  pricing: ItemPricingState;
}) {
  const quantities = [...quantityBreaks].sort((a, b) => a - b);
  const rowsByQuantity = new Map(
    (pricing.status === "ready" ? pricing.preview.quantityLadder : []).map(
      (row) => [row.quantity, row],
    ),
  );
  /**
   * True while this rung is still waiting on its own price.
   *
   * The page prices the selected quantity first and the rest of the ladder
   * second, so between those two answers every other rung has been asked for
   * but not answered. Only "loading" counts: once the ladder call has landed
   * (or failed) a rung with no row has been answered — there is no price —
   * and a dash is the honest cell.
   */
  const awaitingOwnPrice = (quantity: number) =>
    pricing.status === "ready" &&
    pricing.ladderStatus === "loading" &&
    !rowsByQuantity.has(quantity);
  const accent = (on: boolean) =>
    on
      ? "border-l-2 border-[color:var(--color-primary)]"
      : "border-l-2 border-transparent";

  /**
   * The per-unit and price columns for one row: this row's resolved total, or
   * the reason it has none. Never a list price — "not costed" is the honest
   * answer and the only other thing this cell may say.
   */
  const moneyCells = (
    rowQuotedPence: number | null,
    quantity: number,
    rowLevels: PriceLevels | undefined,
    isHeadline: boolean,
  ) => {
    const muted =
      "px-2 py-2.5 sm:px-3 text-right tabular-nums text-muted-foreground";
    if (pricing.status === "loading") {
      return (
        <>
          <td className={muted}>…</td>
          <td className={muted}>…</td>
        </>
      );
    }
    // This rung's own price is still coming: the headline call answered the
    // selected quantity and the ladder call has not landed yet. A spinner,
    // not a dash — a dash means "we asked and there is no price", and these
    // rungs have not been answered yet.
    if (awaitingOwnPrice(quantity)) {
      return (
        <>
          <td className={muted} />
          <td className={muted}>
            <Loader2
              role="status"
              aria-label={`Pricing ${quantity.toLocaleString()}`}
              className="ml-auto size-3.5 animate-spin"
            />
          </td>
        </>
      );
    }
    if (pricing.status === "error" || rowQuotedPence == null || quantity <= 0) {
      return (
        <>
          <td className={muted}>—</td>
          <td className="px-2 py-2.5 sm:px-3 text-right text-xs text-muted-foreground">
            {quantity <= 0
              ? "—"
              : pricing.status === "error"
                ? "—"
                : pricing.status === "blocked"
                  ? "resolve fields first"
                  : "not costed"}
          </td>
        </>
      );
    }
    const { pence: totalPence, level } = levelPence(
      rowQuotedPence,
      rowLevels,
      selection.priceLevel,
      selection.customPricePence,
      isHeadline,
    );
    return (
      <>
        <td className={muted}>{perUnitLabel(totalPence / 100 / quantity)}</td>
        <td className="px-2 py-2.5 sm:px-3 text-right tabular-nums">
          <div className="font-semibold">{formatQuotedPrice(totalPence)}</div>
          <div className="text-[11px] leading-tight text-muted-foreground">
            {level === "contracted"
              ? "contracted"
              : level === "last_ordered"
                ? "last ordered"
                : level === "similar_ordered"
                  ? "similar order"
                  : level === "last_quoted"
                    ? "last quoted"
                    : level === "custom"
                      ? "custom"
                      : rowLevels?.levels.some((l) => l.kind !== "policy")
                        ? "policy"
                        : ""}
          </div>
        </td>
      </>
    );
  };

  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
            <th className="px-2 py-2 sm:px-3 text-left font-medium">
              Quantity
            </th>
            <th className="px-2 py-2 sm:px-3 text-right font-medium">Saving</th>
            <th className="px-2 py-2 sm:px-3 text-right font-medium">
              Per unit
            </th>
            <th className="px-2 py-2 sm:px-3 text-right font-medium">Price</th>
          </tr>
        </thead>
        <tbody>
          {quantities.map((qty) => {
            const selected = !selection.customQty && selection.quantity === qty;
            const row = rowsByQuantity.get(qty);
            const priced = row?.availability === "priced";
            const total =
              priced && row.quotedPence != null ? row.quotedPence : null;
            return (
              <tr
                key={qty}
                onClick={() => onSelect({ quantity: qty, customQty: false })}
                className={`cursor-pointer border-b border-border last:border-0 ${
                  selected
                    ? "bg-[color:var(--color-primary)]/5"
                    : "hover:bg-muted"
                }`}
              >
                <td
                  className={`px-2 py-2.5 sm:px-3 ${accent(selected)} ${selected ? "font-medium" : ""}`}
                >
                  <span className="flex items-center gap-2">
                    {selected && (
                      <Check className="size-3.5 text-[color:var(--color-primary)]" />
                    )}
                    {qty.toLocaleString()}
                  </span>
                </td>
                <td className="px-2 py-2.5 sm:px-3 text-right">
                  {!priced ? null : row.isBestValue ? (
                    <span className="whitespace-nowrap rounded-full bg-[color:var(--color-success)]/10 px-2 py-0.5 text-xs text-[color:var(--color-success)]">
                      best value
                    </span>
                  ) : (row.savingPct ?? 0) > 0 ? (
                    <span className="text-xs text-[color:var(--color-success)]">
                      −{row.savingPct}%
                    </span>
                  ) : null}
                </td>
                {/* Per unit is this row's own total over its own quantity —
                    the selected row's total is the headline's, by
                    construction, so the two read the same. */}
                {moneyCells(total, qty, row?.priceLevels, selected)}
              </tr>
            );
          })}
          {/* Custom — a normal row: an input in the Quantity column, priced by
              the same response as the presets (a typed quantity is what the
              headline resolved, so it is read from there rather than
              interpolated between list prices, as it used to be).
              Clicking any preset row simply switches selection away from it. */}
          {(() => {
            const on = selection.customQty;
            const headlinePence =
              pricing.status === "ready" ? pricing.preview.quotedPence : null;
            return (
              <tr
                className={`border-t border-border ${on ? "bg-[color:var(--color-primary)]/5" : ""}`}
              >
                <td className={`px-2 py-2.5 sm:px-3 ${accent(on)}`}>
                  <span className="flex items-center gap-2">
                    {on && (
                      <Check className="size-3.5 text-[color:var(--color-primary)]" />
                    )}
                    <input
                      type="number"
                      value={
                        on && selection.quantity > 0 ? selection.quantity : ""
                      }
                      placeholder="Custom…"
                      onFocus={() => {
                        // Start blank rather than inheriting the selected preset.
                        if (!on) onSelect({ customQty: true, quantity: 0 });
                      }}
                      onChange={(e) =>
                        onSelect({
                          customQty: true,
                          quantity: Math.max(
                            0,
                            parseInt(e.target.value, 10) || 0,
                          ),
                        })
                      }
                      className="w-24 rounded-md border border-border bg-card px-2 py-1 text-base sm:text-sm"
                    />
                  </span>
                </td>
                <td className="px-2 py-2.5 sm:px-3 text-right" />
                {on ? (
                  moneyCells(
                    headlinePence,
                    selection.quantity,
                    pricing.status === "ready"
                      ? pricing.preview.priceLevels
                      : undefined,
                    true,
                  )
                ) : (
                  <>
                    <td className="px-2 py-2.5 sm:px-3 text-right tabular-nums text-muted-foreground">
                      —
                    </td>
                    <td className="px-2 py-2.5 sm:px-3 text-right font-semibold tabular-nums">
                      —
                    </td>
                  </>
                )}
              </tr>
            );
          })()}
        </tbody>
      </table>
    </div>
  );
}
